import { useStore } from '@common/components/StoreProvider';
import { IBusinessUnitModel } from '@common/models/Businesses/BusinessUnit';

export type ServiceId = number;
export type BusinessUnitId = number;
export type ConfigKey = `${ServiceId}-${BusinessUnitId}`;

export interface ChatConfig<T> {
	DEFAULT: T;
	CONFIG: Record<ConfigKey, T>;
}

/**
 * Function to find the matching service and business unit based on the configuration.
 * @param {Config<T>} config - The configuration object.
 * @param {number} serviceId - The ID representing the selected service.
 * @param {IBusinessUnitModel[]} businessUnits - List of business units.
 * @returns {{ serviceId: number; businessUnitId: number } | null} - Returns
 * an object with service and business unit IDs or null if no match is found.
 */
export function findConfigKey<T>(
	config: ChatConfig<T>,
	serviceId: number,
	businessUnits: IBusinessUnitModel[],
): { serviceId: number; businessUnitId: number } | null {
	const matchingUnit = businessUnits.find((unit) => {
		const key = `${serviceId}-${unit.id}` as ConfigKey;
		return key in config.CONFIG;
	});

	if (!matchingUnit) {
		return null;
	}

	return { serviceId, businessUnitId: matchingUnit.id };
}

/**
 * Generic function to get configuration value based on serviceId and business unit.
 * @param {Config<T>} config - The configuration object.
 * @param {number} serviceId - The ID representing the selected service.
 * @param {IBusinessUnitModel[]} businessUnits - List of business units.
 * @returns {T} - Returns the corresponding value from the configuration or the default value.
 */
export function getConfigValue<T>(
	config: ChatConfig<T>,
	serviceId: number,
	businessUnits: IBusinessUnitModel[],
): T {
	const match = findConfigKey(config, serviceId, businessUnits);

	if (!match) {
		return config.DEFAULT;
	}

	const key = `${match.serviceId}-${match.businessUnitId}` as ConfigKey;
	return config.CONFIG[key];
}

/**
 * Universal hook for accessing configuration values or details.
 * This hook uses functions provided as parameters to calculate results
 * based on the current `serviceId` and `businessUnits` from the store.
 *
 * @template T - The return type of the provided function.
 * @param {Function} configFunction - A function that takes `serviceId`
 * and `businessUnits` as parameters and returns a result.
 * @returns {T} - The result of the provided function.
 */
export function useConfig<T>(
	configFunction: (serviceId: number, businessUnits: IBusinessUnitModel[]) => T,
): T {
	const store = useStore();
	const serviceId = store.services.current.id;
	const businessUnits = store.profile.businesses;

	return configFunction(serviceId, businessUnits);
}
